import { useState } from 'react';
import { createPortal } from 'react-dom';
import { alert, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';

/**
 * Cancelling, with a reason. Used from the day schedule, from a client's
 * page, and for clearing someone's whole schedule.
 *
 * One component rather than three copies, because three copies is how the
 * client page ended up with a bare "cancelled by coach" while the day
 * sheet had proper reasons.
 *
 * The reason is the point. A client who reads "your session is cancelled"
 * wonders what they did wrong; one who reads "cancelled — rain, courts
 * flooded" just gets on with their day.
 */

export const CANCEL_REASONS: [string, 'co.reason.rain' | 'co.reason.noCourt'
  | 'co.reason.illness' | 'co.reason.emergency' | 'co.reason.other'][] = [
  ['rain', 'co.reason.rain'],
  ['court', 'co.reason.noCourt'],
  ['illness', 'co.reason.illness'],
  ['emergency', 'co.reason.emergency'],
  ['other', 'co.reason.other'],
];

const PHRASING: Record<string, string> = {
  rain: 'rain',
  court: 'no court available',
  illness: 'illness',
  emergency: 'an emergency',
  other: 'unforeseen circumstances',
};

export function CancelSheet({
  title,
  subtitle,
  confirmLabel,
  warning,
  onConfirm,
  onClose,
}: {
  title: string;
  subtitle: string;
  confirmLabel: string;
  warning?: string;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('other');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      // Whatever is typed here lands verbatim in the client's message.
      const full = note ? `${PHRASING[reason]} — ${note}` : PHRASING[reason];
      await onConfirm(full);
      haptic.success();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel">
        <div className="sheet__title">{title}</div>
        <div className="card__when">{subtitle}</div>
        {warning && <div className="warnline">{warning}</div>}

        <div className="sheet__label">{t('co.today.why')}</div>
        <div className="repeat__opts">
          {CANCEL_REASONS.map(([id, key]) => (
            <button
              key={id}
              className="chip"
              aria-pressed={reason === id}
              onClick={() => { haptic.tap(); setReason(id); }}
            >
              {t(key)}
            </button>
          ))}
        </div>

        <input
          className="paybar__opt"
          style={{ width: '100%', textAlign: 'left', margin: '10px 0' }}
          placeholder={t('co.cs.note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={onClose}>
            {t('co.pkg.back')}
          </button>
          <button
            className="paybar__opt"
            style={{ flex: 2, background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }}
            disabled={busy}
            onClick={go}
          >
            {busy ? t('co.cs.cancelling') : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
